import { useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Minus, Plus, X } from "lucide-react";
import { api, inr, PLACEHOLDER } from "@/lib/api";
import { useCart, usePageMeta, useSite } from "@/lib/store";
import { WRAP } from "@/components/store/Chrome";

export default function Basket() {
  const cart = useCart();
  const { site } = useSite();
  const nav = useNavigate();
  usePageMeta(site?.pages?.basket?.title, site?.pages?.basket?.description, site?.pages?.basket?.keywords);
  const ids = cart.items.map((i) => i.product_id).join(",");

  useEffect(() => {
    if (ids) api.post("/products/lookup", { ids: ids.split(",") }).then((r) => cart.refresh(r.data));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ids]);

  const ship = site?.shipping;
  const blocked = cart.items.some((i) => !i.snapshot.stock || i.qty > i.snapshot.stock);
  return (
    <div className={`${WRAP} pt-12 max-w-5xl`}>
      <p className="label-caps text-[11px] text-red mb-1.5">Your crate</p>
      <h1 className="font-head font-extrabold text-4xl tracking-tight border-b-2 border-black pb-4" data-testid="basket-title">Basket</h1>
      {cart.items.length === 0 ? (
        <div className="py-24 text-center" data-testid="basket-empty">
          <p className="text-[#777] mb-6">Your basket is empty.</p>
          <Link to="/shop" className="bg-black text-white px-6 py-3 text-xs font-bold tracking-[0.15em] hover:bg-[#dc1f26]">DIG THE CRATES</Link>
        </div>
      ) : (
        <div className="grid lg:grid-cols-[1fr_320px] gap-10 mt-8">
          <ul className="divide-y divide-[#e5e5e5]" data-testid="basket-items">
            {cart.items.map(({ product_id: id, qty, snapshot: s }) => (
              <li key={id} className="py-5 flex gap-4 items-center" data-testid={`basket-item-${id}`}>
                <Link to={`/record/${id}`} className="w-20 h-20 bg-white border border-[#e3e3e3] shrink-0">
                  <img src={s.image || PLACEHOLDER} alt="" className="w-full h-full object-cover" />
                </Link>
                <div className="flex-1 min-w-0">
                  <p className="font-arial font-bold uppercase text-xs">{s.artist}</p>
                  <p className="text-sm truncate">{s.title} <span className="text-[#999]">{s.edition && `· ${s.edition}`}{s.format && ` · ${s.format}`}</span></p>
                  <p className="text-red font-arial font-bold text-sm mt-1">{inr(s.price)}</p>
                  {(!s.stock || qty > s.stock) && <p className="text-xs text-red mt-1">{s.stock ? "Fewer available than in your basket – please reduce the quantity" : "Sold out – please remove"}</p>}
                </div>
                <div className="flex items-center border border-black bg-white">
                  <button onClick={() => cart.setQty(id, qty - 1)} className="w-8 h-9 grid place-items-center" data-testid={`basket-minus-${id}`}><Minus className="w-3.5 h-3.5" /></button>
                  <span className="w-7 text-center text-sm font-arial" data-testid={`basket-qty-${id}`}>{qty}</span>
                  <button onClick={() => cart.setQty(id, Math.min(qty + 1, s.stock || 1))} className="w-8 h-9 grid place-items-center" data-testid={`basket-plus-${id}`}><Plus className="w-3.5 h-3.5" /></button>
                </div>
                <button onClick={() => cart.remove(id)} className="text-[#999] hover:text-red" data-testid={`basket-remove-${id}`}><X className="w-5 h-5" /></button>
              </li>
            ))}
          </ul>
          <aside className="bg-white border border-[#e3e3e3] p-6 h-fit">
            <div className="flex justify-between font-arial"><span>Subtotal</span><b data-testid="basket-subtotal">{inr(cart.subtotal)}</b></div>
            {ship && (
              <p className="text-xs text-[#777] mt-3 leading-relaxed">
                Delivery in India {inr(ship.india_rate)}{ship.india_free ? `, free over ${inr(ship.india_free)}` : ""}. Calculated at checkout.
              </p>
            )}
            <button disabled={blocked} onClick={() => nav("/checkout")} className="mt-6 w-full bg-black text-white py-3.5 text-xs font-bold tracking-[0.15em] hover:bg-[#dc1f26] disabled:opacity-40 transition-colors" data-testid="basket-checkout-button">
              CHECKOUT
            </button>
            <p className="label-caps text-[9px] text-[#999] mt-4 text-center">All prices in INR · Delivered by BlueDart</p>
          </aside>
        </div>
      )}
    </div>
  );
}
